import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { NbIconModule } from '@nebular/theme';
import { ChartConfiguration, ChartData, ChartDataset, ChartOptions } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { LineGraphData, LineGraphSeries } from 'src/app/models/line-graph.models';

const DEFAULT_LINE_COLORS = [
  '#2563eb',
  '#16a34a',
  '#dc2626',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#4f46e5',
  '#65a30d',
  '#be123c',
  '#7c3aed',
  '#0d9488',
  '#ca8a04',
  '#db2777',
  '#0284c7',
  '#059669',
  '#b91c1c',
  '#6d28d9',
  '#c2410c',
  '#0f766e',
  '#4338ca',
  '#84cc16',
  '#e11d48',
  '#a855f7',
  '#f97316',
  '#06b6d4',
  '#22c55e',
  '#ef4444',
  '#8b5cf6',
  '#f59e0b',
  '#14b8a6'
];

@Component({
  selector: 'app-line-graph',
  templateUrl: './line-graph.component.html',
  styleUrls: ['./line-graph.component.scss'],
  imports: [NgChartsModule, CommonModule, NbIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LineGraphComponent implements OnChanges {
  @Input() graphData: LineGraphData = {
    labels: [],
    series: []
  };

  @Input() height = 360;

  @Input() maxVisibleLines: number | null = 8;

  @Output() reload = new EventEmitter<void>();

  legendFilter = '';
  hoveredLineId: string | null = null;

  private hiddenLineIds = new Set<string>();
  private hasInitializedVisibility = false;

  readonly chartType: ChartConfiguration<'line'>['type'] = 'line';

  readonly chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true
      }
    },
    scales: {
      x: {
        ticks: {
          autoSkip: true,
          maxRotation: 0
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: $localize`Deliveries`,
          color: '#344054',
          font: {
            size: 13,
            weight: 500
          }
        }
      }
    }
  };

  ngOnChanges(): void {
    this.removeStaleHiddenLineIds();

    if (!this.hasInitializedVisibility && this.series.length > 0) {
      this.applyInitialVisibility();
      this.hasInitializedVisibility = true;
    }

    if (this.hoveredLineId && !this.series.some((line) => line.id === this.hoveredLineId)) {
      this.hoveredLineId = null;
    }
  }

  get chartData(): ChartData<'line'> {
    return {
      labels: this.labels,
      datasets: this.series.map((line, index) => this.toChartDataset(line, index))
    };
  }

  get filteredSeries(): LineGraphSeries[] {
    const filter = this.legendFilter.trim().toLowerCase();

    if (!filter) {
      return this.series;
    }

    return this.series.filter((line) => line.label.toLowerCase().includes(filter));
  }

  get visibleLineCount(): number {
    return this.series.filter((line) => !this.hiddenLineIds.has(line.id)).length;
  }

  get totalLineCount(): number {
    return this.series.length;
  }

  get hasData(): boolean {
    return this.labels.length > 0 && this.series.length > 0;
  }

  isHidden(lineId: string): boolean {
    return this.hiddenLineIds.has(lineId);
  }

  toggleLine(lineId: string): void {
    const nextHiddenLineIds = new Set(this.hiddenLineIds);

    if (nextHiddenLineIds.has(lineId)) {
      nextHiddenLineIds.delete(lineId);
    } else {
      nextHiddenLineIds.add(lineId);
    }

    this.hiddenLineIds = nextHiddenLineIds;
  }

  showAll(): void {
    this.hiddenLineIds = new Set<string>();
  }

  hideAll(): void {
    this.hiddenLineIds = new Set(this.series.map((line) => line.id));
  }

  showOnly(lineId: string): void {
    this.hiddenLineIds = new Set(this.series.filter((line) => line.id !== lineId).map((line) => line.id));

    this.hoveredLineId = lineId;
  }

  setHoveredLine(lineId: string | null): void {
    this.hoveredLineId = lineId;
  }

  onLegendFilterInput(event: Event): void {
    this.legendFilter = (event.target as HTMLInputElement).value;
  }

  clearLegendFilter(): void {
    this.legendFilter = '';
  }

  getLineColor(lineId: string): string {
    const lineIndex = this.series.findIndex((line) => line.id === lineId);
    const line = this.series[lineIndex];

    return line?.borderColor ?? DEFAULT_LINE_COLORS[lineIndex % DEFAULT_LINE_COLORS.length];
  }

  trackByLineId(_: number, line: LineGraphSeries): string {
    return line.id;
  }

  private get labels(): string[] {
    return this.graphData?.labels ?? [];
  }

  private get series(): LineGraphSeries[] {
    return this.graphData?.series ?? [];
  }

  private applyInitialVisibility(): void {
    if (this.maxVisibleLines === null) {
      return;
    }

    const maxVisibleLines = Math.max(0, this.maxVisibleLines);

    this.hiddenLineIds = new Set(this.series.slice(maxVisibleLines).map((line) => line.id));
  }

  private removeStaleHiddenLineIds(): void {
    const currentLineIds = new Set(this.series.map((line) => line.id));

    this.hiddenLineIds = new Set(Array.from(this.hiddenLineIds).filter((lineId) => currentLineIds.has(lineId)));
  }

  private toChartDataset(line: LineGraphSeries, index: number): ChartDataset<'line', number[]> {
    const baseColor = line.borderColor ?? DEFAULT_LINE_COLORS[index % DEFAULT_LINE_COLORS.length];

    const isHovered = this.hoveredLineId === line.id;
    const shouldFade = this.hoveredLineId !== null && this.hoveredLineId !== line.id;

    return {
      label: line.label,
      data: line.data,
      borderColor: shouldFade ? colorWithAlpha(baseColor, 0.18) : baseColor,
      backgroundColor: shouldFade
        ? colorWithAlpha(line.backgroundColor ?? baseColor, 0.08)
        : (line.backgroundColor ?? colorWithAlpha(baseColor, 0.18)),
      pointBackgroundColor: shouldFade ? colorWithAlpha(baseColor, 0.18) : baseColor,
      pointBorderColor: shouldFade ? colorWithAlpha(baseColor, 0.18) : baseColor,
      borderWidth: isHovered ? 3 : shouldFade ? 1 : 2,
      pointRadius: shouldFade ? 1 : 2.5,
      pointHoverRadius: isHovered ? 6 : 4,
      tension: 0.25,
      fill: false,
      spanGaps: true,
      hidden: this.hiddenLineIds.has(line.id)
    };
  }

  public getVisibilityTitle(line: LineGraphSeries): string {
    return this.isHidden(line.id) ? $localize`Show line` : $localize`Hide line`;
  }

  public getToggleAriaLabel(line: LineGraphSeries): string {
    return this.isHidden(line.id) ? $localize`Show ${line.label}` : $localize`Hide ${line.label}`;
  }

  public getFocusAriaLabel(line: LineGraphSeries): string {
    return $localize`Show only ${line.label}`;
  }
}

function colorWithAlpha(color: string, alpha: number): string {
  const hex = color.replace('#', '').trim();

  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const rgbMatch = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);

  if (rgbMatch) {
    const [, r, g, b] = rgbMatch;

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return color;
}
